import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Box,
  IconButton,
  SelectChangeEvent,
} from '@mui/material';
import { Close as CloseIcon, Add as AddIcon, Edit as EditIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../services/useApi';

export interface CardForForm {
  id: number;
  slot: number;
  model?: string;
  qtde_portas: number;
  serialnumber?: string;
  sw?: string;
  status?: string;
  description?: string;
}

interface OlTCardFormProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  oltId: string;
  /** Quando informado, o formulário abre em modo edição (PUT) */
  card?: CardForForm | null;
}

const OlTCardForm: React.FC<OlTCardFormProps> = ({ open, onClose, onSuccess, oltId, card }) => {
  const isEdit = Boolean(card?.id);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    slot: '',
    model: '',
    qtde_portas: '',
    serialnumber: '',
    sw: '',
    status: 'active' as 'active' | 'inactive' | 'maintenance',
    description: '',
  });

  // Preencher formulário quando abrir para edição
  React.useEffect(() => {
    if (open && card) {
      setFormData({
        slot: String(card.slot),
        model: card.model || '',
        qtde_portas: String(card.qtde_portas),
        serialnumber: card.serialnumber || '',
        sw: card.sw || '',
        status: (card.status as 'active' | 'inactive' | 'maintenance') || 'active',
        description: card.description || '',
      });
    } else if (open && !card) {
      setFormData({
        slot: '',
        model: '',
        qtde_portas: '',
        serialnumber: '',
        sw: '',
        status: 'active',
        description: '',
      });
    }
  }, [open, card]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const name = e.target.name as keyof typeof formData;
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError(null);
  };

  const handleStatusChange = (e: SelectChangeEvent<'active' | 'inactive' | 'maintenance'>) => {
    setFormData((prev) => ({ ...prev, status: e.target.value as 'active' | 'inactive' | 'maintenance' }));
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const qtde_portas = parseInt(formData.qtde_portas, 10);
    if (!formData.qtde_portas || isNaN(qtde_portas) || qtde_portas < 1 || qtde_portas > 128) {
      setError('Quantidade de portas é obrigatória (1 a 128)');
      return;
    }

    if (!isEdit) {
      const slot = parseInt(formData.slot, 10);
      if (!formData.slot || isNaN(slot) || slot < 0) {
        setError('Slot é obrigatório e deve ser um número válido');
        return;
      }
    }

    setLoading(true);
    try {
      if (isEdit && card) {
        const response = await api.put(`/olts/cards/${card.id}`, {
          model: formData.model || undefined,
          qtde_portas,
          serialnumber: formData.serialnumber || undefined,
          sw: formData.sw || undefined,
          status: formData.status,
          description: formData.description || undefined,
        });
        if (response.data.success) {
          toast.success('Card atualizado com sucesso!');
          onSuccess();
          onClose();
        } else {
          setError(response.data.message || 'Erro ao atualizar card');
        }
      } else {
        const slot = parseInt(formData.slot, 10);
        const response = await api.post(`/olts/${oltId}/cards`, {
          slot,
          model: formData.model || undefined,
          qtde_portas,
          serialnumber: formData.serialnumber || undefined,
          sw: formData.sw || undefined,
          status: formData.status,
          description: formData.description || undefined,
        });
        if (response.data.success) {
          toast.success('Card cadastrado com sucesso!');
          setFormData({
            slot: '',
            model: '',
            qtde_portas: '',
            serialnumber: '',
            sw: '',
            status: 'active',
            description: '',
          });
          onSuccess();
          onClose();
        } else {
          setError(response.data.message || 'Erro ao cadastrar card');
        }
      }
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || (isEdit ? 'Erro ao atualizar card' : 'Erro ao cadastrar card');
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      slot: '',
      model: '',
      qtde_portas: '',
      serialnumber: '',
      sw: '',
      status: 'active',
      description: '',
    });
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 1 } }}>
      <Box component="form" onSubmit={handleSubmit}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box display="flex" alignItems="center" gap={1}>
            {isEdit ? <EditIcon color="primary" /> : <AddIcon color="primary" />}
            {isEdit ? 'Editar Card' : 'Cadastrar Card'}
          </Box>
          <IconButton onClick={handleClose} size="small" sx={{ p: 0.5 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ pt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Slot"
                name="slot"
                value={formData.slot}
                onChange={handleChange}
                required
                type="number"
                inputProps={{ min: 0 }}
                size="small"
                helperText="Número do slot do card"
                disabled={isEdit}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Quantidade de portas"
                name="qtde_portas"
                value={formData.qtde_portas}
                onChange={handleChange}
                required
                type="number"
                inputProps={{ min: 1, max: 128 }}
                size="small"
                helperText="Ex: 8 ou 16"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Modelo"
                name="model"
                value={formData.model}
                onChange={handleChange}
                size="small"
                placeholder="Ex: GPON-8, GPON-16"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Serial Number"
                name="serialnumber"
                value={formData.serialnumber}
                onChange={handleChange}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Software (SW)"
                name="sw"
                value={formData.sw}
                onChange={handleChange}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  value={formData.status}
                  onChange={handleStatusChange}
                  label="Status"
                >
                  <MenuItem value="active">Ativo</MenuItem>
                  <MenuItem value="inactive">Inativo</MenuItem>
                  <MenuItem value="maintenance">Manutenção</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descrição"
                name="description"
                value={formData.description}
                onChange={handleChange}
                size="small"
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={handleClose} color="inherit" disabled={loading}>
            Cancelar
          </Button>
          <Button type="submit" variant="contained" color="primary" disabled={loading}>
            {loading ? (isEdit ? 'Salvando...' : 'Cadastrando...') : (isEdit ? 'Salvar' : 'Cadastrar')}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default OlTCardForm;
